// Ansicht „limit" des Pricing-Dialogs. Zeigt die Nutzung einer Ressource und
// was der Kunde jetzt tun kann. Gesperrt wird hier nichts, das machen die
// Guards vorher. Abschalten, Archivieren, Entfernen und Löschen gehen immer.
//
// Optionen je Ressource
// Stellen: archivieren (Stellenliste mit Filter aktiv), Zusatzstelle anfragen
// solange das Paketmaximum nicht erreicht ist, sonst größeres Paket.
// Zugänge: Teammitglied entfernen (Mitgliederliste), Zugang anfragen bzw.
// größeres Paket.
// Talentspeicher: bei Auswahlaktionen zuerst „Gesperrte abwählen", dann alte
// Talente anzeigen (Filter älter als 6 Monate) und mehr Speicher anfragen.
// Mehr Speicher kommt über Zusatzstellen, am Stellenmaximum also über ein
// größeres Paket.
// Dazu immer „Pakete vergleichen" auf die Preisseite der Landingpage.
import { Description, DialogTitle } from '@headlessui/react'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import {
  ArchiveBoxIcon,
  ArrowTopRightOnSquareIcon,
  ArrowTrendingUpIcon,
  BriefcaseIcon,
  ChartBarIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ClockIcon,
  MinusCircleIcon,
  PlusCircleIcon,
  UserMinusIcon,
  UsersIcon
} from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import ActivityIndicator from 'react-spinners/BeatLoader'

import { usePartnerContext, useUserContext } from '../../context'
import { useEntitlements, useLimit } from '../../features/pricing/hooks'
import { LIMIT_STATES } from '../../features/pricing/limits'
import {
  PLANS,
  PLAN_ORDER,
  STORAGE_PER_ADDON_CAREER
} from '../../features/pricing/plans'
import {
  PRICING_REQUEST_KINDS,
  PRICING_REQUEST_STATUS
} from '../../features/pricing/pricing-slack'
import { getPricingCompareUrl } from '../../features/pricing/use-feature-gate'
import { usePricingRequest } from '../../features/pricing/use-pricing-request'
import { toBase64 } from '../../helpers'
import { localStorage } from '../../helpers/local-storage'
import Button from '../tailwind/Button'
import {
  AdminHint,
  DialogFooter,
  DialogIconRow,
  MeetingLink,
  RequestResult,
  planDisplayName
} from './pricing-dialog-parts'
import UsageMeter from './usage-meter'

// i18n-Pfad der Ressource, im Code heißt der Talentspeicher talentStorage
const I18N_RESOURCE = Object.freeze({
  careers: 'careers',
  seats: 'seats',
  talentStorage: 'talent_storage'
})

const TITLED_STATES = ['warning', 'at_limit', 'over_limit']

// Texte je Aktion. Ohne Treffer gilt text_info.
const ACTION_TEXT = {
  careers: {
    activate: 'text_activate',
    duplicate: 'text_duplicate',
    discover: 'text_discover',
    recover: 'text_recover'
  },
  seats: { invite: 'text_invite' },
  talentStorage: { recover: 'text_recover', selection: 'text_selection' }
}

// Plural-Texte, die count als Zahl brauchen
const COUNTED_TEXTS = ['text_recover', 'text_selection', 'text_locked']

const nextPlanOf = (planId) => {
  const index = PLAN_ORDER.indexOf(planId)
  return index >= 0 ? PLAN_ORDER[index + 1] || null : null
}

// Unter dem Paketmaximum lässt sich zubuchen. custom kennt kein Maximum, dort
// klärt CS die Zusatzmenge.
const belowMax = (planId, resource, limit) => {
  const max = PLANS[planId]?.max?.[resource]
  if (typeof max !== 'number') return true
  if (typeof limit !== 'number') return true
  return limit < max
}

const finiteOrNull = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/**
 * Titel, Text und Anfrage-Option je Ressource, Zustand und Aktion. Rein, damit
 * die Regeln ohne Rendern testbar sind. Texte kommen als { key, values }.
 *
 * @param {object} params
 * @param {string|null} params.planName Paketname, nur bei Plan-Payment oder
 *   Simulation
 * @param {(n: number) => string} params.formatNumber
 */
export const describeLimitDialog = ({
  resource,
  action,
  state,
  used,
  limit,
  count,
  planId,
  planName = null,
  careersLimit = null,
  formatNumber = String
}) => {
  const r = I18N_RESOURCE[resource]
  const base = `pricing.limit.${r}`

  let title = `pricing.resources.${r}.name`
  if (resource === 'talentStorage' && action === 'selection') {
    title = `${base}.title_selection`
  } else if (TITLED_STATES.includes(state)) {
    title = `${base}.title_${state}`
  }

  let textKey = ACTION_TEXT[resource]?.[action] || 'text_info'
  let textCount = typeof count === 'number' ? count : 1
  if (
    resource === 'talentStorage' &&
    textKey === 'text_info' &&
    state === LIMIT_STATES.OVER_LIMIT &&
    typeof used === 'number' &&
    typeof limit === 'number'
  ) {
    textKey = 'text_locked'
    textCount = Math.max(1, used - limit)
  }

  const text = {
    key: `${base}.${textKey}`,
    values: COUNTED_TEXTS.includes(textKey) ? { count: textCount } : {}
  }

  // Mehr Speicher kommt über Zusatzstellen, also zählt dort das Stellenlimit
  if (
    resource === 'talentStorage'
      ? belowMax(planId, 'careers', careersLimit)
      : belowMax(planId, resource, limit)
  ) {
    const option = resource === 'talentStorage' ? 'option_more' : 'option_addon'
    return {
      title,
      text,
      requestOption: {
        kind: PRICING_REQUEST_KINDS.LIMIT,
        key: resource,
        targetPlan: null,
        upgrade: false,
        title: `${base}.${option}_title`,
        text: {
          key: `${base}.${option}_text`,
          values: { storage: STORAGE_PER_ADDON_CAREER }
        }
      }
    }
  }

  // Am Paketmaximum hilft nur ein größeres Paket. Den Satz mit Paketname und
  // Maximum gibt es für Stellen und Zugänge, sonst den allgemeinen.
  const max = finiteOrNull(PLANS[planId]?.max?.[resource])
  let upgradeText = { key: 'pricing.limit.option_upgrade_text', values: {} }
  if (resource === 'talentStorage') {
    upgradeText = { key: `${base}.option_upgrade_text`, values: {} }
  } else if (max !== null && planName) {
    upgradeText = {
      key: `${base}.option_upgrade_text`,
      values: { plan: planName, max: formatNumber(max) }
    }
  }

  return {
    title,
    text,
    requestOption: {
      kind: PRICING_REQUEST_KINDS.PLAN,
      key: resource,
      targetPlan: nextPlanOf(planId),
      upgrade: true,
      title: 'pricing.limit.option_upgrade_title',
      text: upgradeText
    }
  }
}

const OPTION_CLASS =
  'flex w-full items-center gap-x-3 rounded-lg p-3 text-left ring-1 ring-inset ring-gray-200 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 disabled:cursor-default disabled:hover:bg-white'

const OptionContent = ({ icon: Icon, title, text, trailing }) => (
  <>
    <span className='flex size-9 shrink-0 items-center justify-center rounded-md bg-gray-100'>
      <Icon className='size-5 text-gray-500' aria-hidden='true' />
    </span>
    <span className='min-w-0 flex-1'>
      <span className='block text-sm font-medium text-gray-900'>{title}</span>
      <span className='mt-0.5 block text-sm text-gray-500'>{text}</span>
    </span>
    {trailing}
  </>
)

OptionContent.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  trailing: PropTypes.node
}

OptionContent.defaultProps = { trailing: null }

const Chevron = () => (
  <ChevronRightIcon
    className='size-5 shrink-0 text-gray-400'
    aria-hidden='true'
  />
)

// done heißt, die Option ist schon erledigt (bereits angefragt). Sie bleibt
// sichtbar, ist aber nicht mehr klickbar.
const OptionButton = ({ icon, title, text, onClick, loading, done }) => (
  <li>
    <button
      type='button'
      onClick={loading || done ? undefined : onClick}
      disabled={loading || done}
      aria-busy={loading || undefined}
      className={OPTION_CLASS}
    >
      <OptionContent
        icon={icon}
        title={title}
        text={text}
        trailing={
          loading ? (
            <span className='shrink-0' aria-hidden='true'>
              <ActivityIndicator color='#9CA3AF' size={6} />
            </span>
          ) : done ? (
            <CheckCircleIcon
              className='size-5 shrink-0 text-green-600'
              aria-hidden='true'
            />
          ) : (
            <Chevron />
          )
        }
      />
    </button>
  </li>
)

OptionButton.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  done: PropTypes.bool
}

OptionButton.defaultProps = { loading: false, done: false }

const OptionLink = ({ icon, title, text, href }) => (
  <li>
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      className={OPTION_CLASS}
    >
      <OptionContent
        icon={icon}
        title={title}
        text={text}
        trailing={
          <ArrowTopRightOnSquareIcon
            className='size-5 shrink-0 text-gray-400'
            aria-hidden='true'
          />
        }
      />
    </a>
  </li>
)

OptionLink.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired
}

const RESOURCE_ICONS = {
  careers: BriefcaseIcon,
  seats: UsersIcon,
  talentStorage: CircleStackIcon
}

const ACTIVE_CAREERS_FILTER = [{ status: { in: ['ACTIVE'] } }]
const OLD_TALENTS_FILTER = [{ more: { in: ['BEFORE_6_MONTH'] } }]

const LimitDialog = ({ resource, source, context, onClose }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const entitlements = useEntitlements()
  const { partner } = usePartnerContext()
  const { isAdmin } = useUserContext()
  const limitState = useLimit(resource)

  const action = context?.action || 'info'
  const used = limitState.used ?? context?.used ?? null
  const limit = limitState.limit ?? context?.limit ?? null
  const plan = entitlements.plan
  const planId = plan?.id ?? null

  const formatNumber = (n) => n.toLocaleString(i18n.language)

  const { title, text, requestOption } = describeLimitDialog({
    resource,
    action,
    state: limitState.state,
    used,
    limit,
    count: context?.count,
    planId,
    planName: plan ? planDisplayName(t, plan) : null,
    careersLimit: entitlements.limits?.careers?.limit ?? null,
    formatNumber
  })

  const request = usePricingRequest({
    kind: requestOption.kind,
    key: requestOption.key,
    targetPlan: requestOption.targetPlan,
    source,
    usage: { resource, used, limit }
  })
  // Die Bestätigung ersetzt den Inhalt nur nach einer Anfrage aus diesem
  // Dialog. Liegt im Browser schon eine vor, startet der Hook in duplicate.
  // Dann bleiben Grund, Nutzung und die übrigen Wege stehen, nur die
  // Anfrage-Option zeigt den Stand.
  const [requestedHere, setRequestedHere] = useState(false)

  if (request.isDone && requestedHere) {
    return <RequestResult status={request.status} onClose={onClose} />
  }

  const alreadyRequested = request.status === PRICING_REQUEST_STATUS.DUPLICATE

  const handleRequest = () => {
    setRequestedHere(true)
    request.send()
  }

  const r = I18N_RESOURCE[resource]
  const resourceName = t(`pricing.resources.${r}.name`)

  // Wie die bestehenden Filter-Links (career/overview.js), die Listen lesen
  // den gespeicherten Filter beim Mounten. Ist die Liste schon offen, greift
  // die neue Suche direkt.
  const goToFiltered = (pathname, param, filter) => {
    const search = `${param}=${encodeURIComponent(toBase64(filter))}`
    localStorage.setItem(pathname, search)
    onClose()
    navigate({ pathname, search })
  }

  const goToTeam = () => {
    onClose()
    if (location.pathname !== '/settings/user') {
      navigate('/settings/user')
    }
  }

  const options = []

  if (
    resource === 'talentStorage' &&
    action === 'selection' &&
    typeof context?.onDeselect === 'function'
  ) {
    options.push(
      <OptionButton
        key='deselect'
        icon={MinusCircleIcon}
        title={t(`pricing.limit.${r}.option_deselect_title`)}
        text={t(`pricing.limit.${r}.option_deselect_text`)}
        onClick={() => {
          context.onDeselect()
          onClose()
        }}
      />
    )
  }

  if (resource === 'careers') {
    options.push(
      <OptionButton
        key='archive'
        icon={ArchiveBoxIcon}
        title={t(`pricing.limit.${r}.option_archive_title`)}
        text={t(`pricing.limit.${r}.option_archive_text`)}
        onClick={() =>
          goToFiltered('/careers', 'filter', ACTIVE_CAREERS_FILTER)
        }
      />
    )
  }

  if (resource === 'seats') {
    options.push(
      <OptionButton
        key='remove'
        icon={UserMinusIcon}
        title={t(`pricing.limit.${r}.option_remove_title`)}
        text={t(`pricing.limit.${r}.option_remove_text`)}
        onClick={goToTeam}
      />
    )
  }

  if (resource === 'talentStorage') {
    options.push(
      <OptionButton
        key='old'
        icon={ClockIcon}
        title={t(`pricing.limit.${r}.option_old_title`)}
        text={t(`pricing.limit.${r}.option_old_text`)}
        onClick={() => goToFiltered('/talents', 'computed', OLD_TALENTS_FILTER)}
      />
    )
  }

  options.push(
    <OptionButton
      key='request'
      icon={requestOption.upgrade ? ArrowTrendingUpIcon : PlusCircleIcon}
      title={t(requestOption.title)}
      text={
        alreadyRequested
          ? t('pricing.dialog.already_sent_text')
          : t(requestOption.text.key, requestOption.text.values)
      }
      onClick={handleRequest}
      loading={request.isSending}
      done={alreadyRequested}
    />
  )

  options.push(
    <OptionLink
      key='compare'
      icon={ChartBarIcon}
      title={t('pricing.limit.option_compare_title')}
      text={t('pricing.limit.option_compare_text')}
      href={getPricingCompareUrl(i18n.language)}
    />
  )

  const countFailed = limitState.status === 'error' && used === null

  return (
    <>
      <div className='px-6 pb-6 pt-6'>
        <DialogIconRow icon={RESOURCE_ICONS[resource]} onClose={onClose} />

        <DialogTitle className='mt-4 text-lg font-semibold text-gray-900'>
          {t(title)}
        </DialogTitle>
        <Description className='mt-2 text-sm text-gray-600'>
          {t(text.key, text.values)}
        </Description>

        <div className='mt-5'>
          <UsageMeter
            used={used}
            limit={limit}
            label={resourceName}
            size='md'
            failed={countFailed}
          />
          {countFailed && (
            <p className='mt-2 text-xs text-gray-500'>
              {t('pricing.usage.count_failed')}
            </p>
          )}
        </div>

        <h3 className='mt-6 text-sm font-medium text-gray-900'>
          {t('pricing.limit.options_label')}
        </h3>
        <ul role='list' className='mt-3 space-y-2'>
          {options}
        </ul>

        {isAdmin && (
          <div className='mt-5'>
            <AdminHint partnerId={partner?.id} />
          </div>
        )}

        {request.isError && (
          <p role='alert' className='mt-3 text-sm text-red-600'>
            {t('pricing.dialog.error_text')}
          </p>
        )}
        <span className='sr-only' aria-live='polite'>
          {request.isSending ? t('pricing.dialog.sending_text') : ''}
        </span>
      </div>

      <DialogFooter>
        <MeetingLink
          tracking={{
            view: 'limit',
            resource,
            action,
            source,
            plan: planId,
            mode: entitlements.mode
          }}
        />
        <Button.SecondaryLG
          text={t('pricing.dialog.cancel_action')}
          onClick={onClose}
        />
      </DialogFooter>
    </>
  )
}

LimitDialog.propTypes = {
  resource: PropTypes.oneOf(['careers', 'seats', 'talentStorage']).isRequired,
  source: PropTypes.string,
  context: PropTypes.shape({
    action: PropTypes.string,
    count: PropTypes.number,
    onDeselect: PropTypes.func,
    used: PropTypes.number,
    limit: PropTypes.number
  }),
  onClose: PropTypes.func.isRequired
}

LimitDialog.defaultProps = {
  source: null,
  context: {}
}

export default LimitDialog
